import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

// Admin "Statistiky" (spec 17): pick a period (round / part / whole season) and league(s), click
// Vygenerovat, get record leaderboards from the season-stats Edge Function. The result can be turned
// into a draft Aktualita, which then opens in the Obsah webu editor.

private const val ALL_LEAGUES = ""

/** One column group of a table: a server category key, headed `label` (null = single-group table, no group header). */
private data class StatsSection(val key: String, val label: String?)

/**
 * One table; paired gender categories share a table as column groups side by side
 * (row i = i-th performance of each), each group with its own rank column.
 */
private data class StatsCategory(
    val title: String,
    val valueLabel: String,
    val showPlayer: Boolean,
    val sections: List<StatsSection>,
    val detailLabel: String? = null,
    val mergedKey: String? = null,
    val kind: String = "performance",
    val byLeagueKey: String? = null,
)

private val STATS_LIMITS = listOf(1, 3, 5, 10, 20)
private data class StatsOptions(
    val enabled: Boolean = true,
    val details: Boolean = true,
    val genderMode: String = "split",
    val byLeague: Boolean = false,
    val limit: Int? = null,
)
private data class StatsView(val category: StatsCategory, val details: Boolean, val limit: Int)

private fun genderSections(prefix: String) = listOf(StatsSection("${prefix}Male", "Muži"), StatsSection("${prefix}Female", "Ženy"))

private val CATEGORIES = listOf(
    StatsCategory("Nejvyšší nához", "Nához", true, genderSections("highestGame"), mergedKey = "highestGameAll"),
    StatsCategory("Nejlepší výkon", "Kuželky", true, genderSections("bestSeries"), "Body", "bestSeriesAll"),
    StatsCategory("Nejlepší výkon družstva", "Kuželky", false, listOf(StatsSection("bestTeam", null)), "Rozdíl kuželek"),
    StatsCategory("Smolař — nejvyšší nához bez bodu", "Nához", true, listOf(StatsSection("unlucky", null))),
    StatsCategory("Štístko — nejnižší nához s bodem", "Nához", true, listOf(StatsSection("lucky", null))),
    StatsCategory("Smolař družstvo — nejvyšší rozdíl při prohře", "Rozdíl kuželek", false, listOf(StatsSection("unluckyTeam", null)), "Body"),
    StatsCategory("Štístko družstvo — nejnižší rozdíl při výhře", "Rozdíl kuželek", false, listOf(StatsSection("luckyTeam", null)), "Body"),
    StatsCategory("Specialista na těsné duely", "Úspěšnost", true, genderSections("tightDuelSpecialist"), mergedKey = "tightDuelSpecialistAll", kind = "specialist"),
    StatsCategory("Nejlepší bodovač", "Úspěšnost", true, genderSections("bestScorer"), mergedKey = "bestScorerAll", kind = "scorer"),
    StatsCategory("Nejtěsnější zápas — remíza nebo 5:4", "Rozdíl kuželek", false, listOf(StatsSection("closestByResult", null)), kind = "match"),
    StatsCategory("Nejtěsnější zápas — bez ohledu na skóre", "Rozdíl kuželek", false, listOf(StatsSection("closestAll", null)), kind = "match"),
    StatsCategory("Průměrný nához", "Průměrný nához", false, listOf(StatsSection("averageOverall", null)), kind = "average", byLeagueKey = "averageByLeague"),
    StatsCategory("Rozdělení zápasů podle skóre", "Počet zápasů", false, listOf(StatsSection("distributionOverall", null)), kind = "distribution", byLeagueKey = "distributionByLeague"),
)

private fun StatsCategory.isAggregate(): Boolean = kind == "average" || kind == "distribution"
private fun StatsCategory.isScoreMatrix(): Boolean = kind == "distribution" && sections.singleOrNull()?.key == byLeagueKey

private fun selectedViews(options: Map<String, StatsOptions>, mainLimit: Int): List<StatsView> = CATEGORIES.mapNotNull { cat ->
    val option = options[cat.sections.first().key] ?: StatsOptions()
    if (!option.enabled) return@mapNotNull null
    val sections = if (cat.byLeagueKey != null && option.byLeague) listOf(StatsSection(cat.byLeagueKey, null))
    else if (cat.mergedKey == null) cat.sections else when (option.genderMode) {
        "all" -> listOf(StatsSection(cat.mergedKey, null))
        "male" -> cat.sections.take(1)
        "female" -> cat.sections.takeLast(1)
        else -> cat.sections
    }
    val title = if (cat.mergedKey != null) cat.title + when (option.genderMode) {
        "all" -> " — společně"
        "male" -> " — muži"
        "female" -> " — ženy"
        else -> ""
    } else cat.title + if (cat.byLeagueKey != null) (if (option.byLeague) " — podle ligy" else " — celkem") else ""
    val category = cat.copy(title = title, sections = sections)
    StatsView(category, option.details && !category.isScoreMatrix(), if (cat.isAggregate()) Int.MAX_VALUE else option.limit ?: mainLimit)
}

private fun SeasonStatsResponseDto.entriesOf(key: String): List<SeasonStatsEntryDto> =
    categories.firstOrNull { it.key == key }?.entries.orEmpty()

private fun baseHeaders(cat: StatsCategory, details: Boolean): List<String> = when (cat.kind) {
    "average" -> listOf("Výběr / liga", cat.valueLabel)
    "distribution" -> if (details) listOf("Výběr / liga", "Skóre", cat.valueLabel, "Podíl") else listOf("Skóre", "Podíl")
    "match" -> listOf("#", "Zápas", "Skóre", cat.valueLabel)
    else -> listOfNotNull("#", if (cat.showPlayer) "Hráč" else null, "Družstvo", cat.valueLabel)
}

private fun baseValues(cat: StatsCategory, e: SeasonStatsEntryDto, details: Boolean): List<String> = when (cat.kind) {
    "average" -> listOf(e.leagueName, e.averageScore?.let { (kotlin.math.round(it * 100) / 100).toString() } ?: "—")
    "distribution" -> listOfNotNull(if (details) e.leagueName else null, e.resultText ?: "—",
        if (details) e.score.toString() else null, "${kotlin.math.round((e.share ?: 0.0) * 1000) / 10} %")
    "match" -> listOf("${e.rank}.", "${e.teamName} – ${e.opponentTeamName}", e.resultText ?: "—", e.score.toString())
    else -> listOfNotNull("${e.rank}.", if (cat.showPlayer) e.playerName ?: "" else null, e.teamName,
        if (cat.kind == "specialist" || cat.kind == "scorer") "${kotlin.math.round((e.successRate ?: 0.0) * 1000) / 10} %" else e.score.toString())
}

private fun detailHeaders(cat: StatsCategory, singleRound: Boolean): List<String> = when (cat.kind) {
    "average" -> listOf("Odehrané hry", "Celkem kuželek", "Zápasy")
    "distribution" -> listOf("Zápasy ve výběru / lize")
    "specialist" -> listOf("Těsné duely", "Výhry", "Remízy", "Prohry", "Minimum duelů", "Liga")
    "scorer" -> listOf("Duely", "Výhry", "Remízy", "Prohry", "Možné hry", "Minimum her", "Liga")
    "match" -> listOf("Kuželky", if (singleRound) "Liga" else "Liga · kolo")
    else -> listOfNotNull(cat.detailLabel, "Soupeř", if (singleRound) "Liga" else "Liga · kolo")
}

private fun detailValues(cat: StatsCategory, e: SeasonStatsEntryDto, singleRound: Boolean): List<String> = when (cat.kind) {
    "average" -> listOf((e.games ?: 0).toString(), (e.totalPins ?: 0).toString(), (e.matchCount ?: 0).toString())
    "distribution" -> listOf((e.matchCount ?: 0).toString())
    "specialist" -> listOf(e.games.toString(), e.wins.toString(), e.draws.toString(),
        ((e.games ?: 0) - (e.wins ?: 0) - (e.draws ?: 0)).toString(), (e.minimumGames ?: 0).toString(), e.leagueName)
    "scorer" -> listOf((e.games ?: 0).toString(), (e.wins ?: 0).toString(), (e.draws ?: 0).toString(),
        ((e.games ?: 0) - (e.wins ?: 0) - (e.draws ?: 0)).toString(),
        (e.possibleGames ?: 0).toString(), (e.minimumGames ?: 0).toString(), e.leagueName)
    "match" -> listOf(e.pinScoreText ?: "—", whereText(e, singleRound))
    else -> listOfNotNull(if (cat.detailLabel != null) detailValue(cat, e) else null,
        opponentText(e).removePrefix("vs "), whereText(e, singleRound))
}

private data class StatsTableGroup(
    val view: StatsView,
    val section: StatsSection,
    val entries: List<SeasonStatsEntryDto>,
    val headers: List<String>,
    val matrixRows: List<List<String>>? = null,
    val accentColumn: Int = baseHeaders(view.category, view.details).lastIndex,
) {
    val label: String get() = view.category.title + (section.label?.let { " — $it" } ?: "")
    val rowCount: Int get() = matrixRows?.size ?: entries.size
}

private data class StatsTable(val views: List<StatsView>, val groups: List<StatsTableGroup>)

private enum class StatsSubject { PLAYER, TEAM, MATCH, SUMMARY }

private fun StatsCategory.subject(): StatsSubject = when (kind) {
    "match" -> StatsSubject.MATCH
    "average", "distribution" -> StatsSubject.SUMMARY
    else -> if (showPlayer) StatsSubject.PLAYER else StatsSubject.TEAM
}

/** Use the same limits, pairing rule and columns for the preview and the news draft. */
private fun statsTables(result: SeasonStatsResponseDto, views: List<StatsView>, singleRound: Boolean): List<StatsTable> {
    val tables = views.map { view ->
        val cat = view.category
        if (cat.isScoreMatrix()) {
            val section = cat.sections.single()
            val entries = result.entriesOf(section.key)
            val leagues = entries.distinctBy { it.leagueId ?: it.leagueName }
            val scores = entries.mapNotNull { it.resultText }.distinct().sortedWith(
                compareBy<String> { it.substringBefore(":").toIntOrNull() ?: 0 }
                    .thenByDescending { it.substringAfter(":").toIntOrNull() ?: 0 }
            )
            val counts = entries.groupBy { (it.leagueId ?: it.leagueName) to it.resultText }
                .mapValues { (_, values) -> values.sumOf { it.score } }
            val rows = scores.map { score ->
                listOf(score) + leagues.map { (counts[(it.leagueId ?: it.leagueName) to score] ?: 0).toString() }
            }
            return@map StatsTable(listOf(view), listOf(StatsTableGroup(
                view, section, emptyList(), listOf("Skóre") + leagues.map { it.leagueName }, rows, -1,
            )))
        }
        val headers = baseHeaders(cat, view.details) + if (view.details) detailHeaders(cat, singleRound) else emptyList()
        StatsTable(listOf(view), cat.sections.map { section ->
            val entries = result.entriesOf(section.key)
            val visible = if (cat.isAggregate()) entries
                else entries.filter { it.rank <= view.limit }
            StatsTableGroup(view, section, visible, headers)
        })
    }
    val out = mutableListOf<StatsTable>()
    var index = 0
    while (index < tables.size) {
        val current = tables[index]
        val next = tables.getOrNull(index + 1)
        val columns = current.groups.sumOf { it.headers.size }
        if (columns <= 5 && next != null &&
            current.views.first().category.subject() == next.views.first().category.subject() &&
            columns + next.groups.sumOf { it.headers.size } <= 10 &&
            current.groups.any { it.rowCount > 0 } && next.groups.any { it.rowCount > 0 }) {
            out += StatsTable(current.views + next.views, current.groups + next.groups)
            index += 2
        } else {
            out += current
            index++
        }
    }
    return out
}

private fun groupValues(group: StatsTableGroup, e: SeasonStatsEntryDto, singleRound: Boolean): List<String> =
    baseValues(group.view.category, e, group.view.details) + if (group.view.details) detailValues(group.view.category, e, singleRound) else emptyList()

private fun groupRowValues(group: StatsTableGroup, row: Int, singleRound: Boolean): List<String>? =
    if (group.matrixRows != null) group.matrixRows.getOrNull(row)
    else group.entries.getOrNull(row)?.let { groupValues(group, it, singleRound) }

private val PARTS = listOf("autumn" to "Podzim", "spring" to "Jaro", "playoff" to "Nadstavba")

/** Period select value: "season" | "part:<autumn|spring|playoff>" | "round:<part>:<n>". */
private data class Period(val phase: String?, val roundNumber: Int?)

private fun parsePeriod(value: String): Period {
    val parts = value.split(":")
    return when (parts[0]) {
        "part" -> Period(parts[1], null)
        "round" -> Period(parts[1], parts[2].toInt())
        else -> Period(null, null)
    }
}

/** O titul + O udržení are one "Nadstavba" part/round here, same as Rozlosování/Hráč kola. */
private fun statsPart(phase: String): String = if (phase == "autumn" || phase == "spring") phase else "playoff"

private fun partLabel(part: String): String = PARTS.firstOrNull { it.first == part }?.second ?: part

private fun periodLabel(period: Period, seasonName: String): String = when {
    period.phase == null -> "celá sezóna $seasonName"
    period.roundNumber == null -> "${partLabel(period.phase)} $seasonName"
    else -> "${partLabel(period.phase)} — kolo ${period.roundNumber}, $seasonName"
}

private fun escapeHtml(s: String): String =
    s.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;").replace("\"", "&quot;")

/** "vs Soupeř (Tým) 187" for single games, "vs Tým" for series/team performances. */
private fun opponentText(e: SeasonStatsEntryDto): String = buildString {
    append("vs ")
    if (e.opponentPlayerName != null) {
        append(e.opponentPlayerName)
        append(" (").append(e.opponentTeamName).append(")")
        e.opponentScore?.let { append(" ").append(it) }
    } else {
        append(e.opponentTeamName)
    }
}

private fun whereText(e: SeasonStatsEntryDto, singleRound: Boolean): String =
    if (singleRound) e.leagueName else "${e.leagueName} · ${roundSlotLabel(e.phase, e.roundNumber)}"

private fun detailValue(cat: StatsCategory, e: SeasonStatsEntryDto): String =
    (if (cat.detailLabel == "Body") e.points else e.pinDifference)?.toString() ?: "—"

/**
 * Body of the draft Aktualita: one table per non-empty category, same layout as the admin view
 * (paired categories side by side). Uses the same section options and columns as the admin view.
 */
private fun buildNewsHtml(result: SeasonStatsResponseDto, subtitle: String, views: List<StatsView>, singleRound: Boolean): String = buildString {
    append("<p>").append(escapeHtml(subtitle)).append("</p>")
    statsTables(result, views, singleRound).forEach { table ->
        val groups = table.groups
        if (groups.all { it.rowCount == 0 }) return@forEach
        if (table.views.size == 1) append("<h3>").append(escapeHtml(table.views.first().category.title)).append("</h3>")
        append("<table>")
        if (groups.size > 1) {
            append("<tr>")
            groups.forEach { group ->
                val label = if (table.views.size > 1) group.label else group.section.label ?: ""
                append("<th colspan=\"").append(group.headers.size).append("\">").append(escapeHtml(label)).append("</th>")
            }
            append("</tr>")
        }
        append("<tr>")
        groups.forEach { group ->
            group.headers.forEach { append("<th>").append(escapeHtml(it)).append("</th>") }
        }
        append("</tr>")
        repeat(groups.maxOf { it.rowCount }) { i ->
            append("<tr>")
            groups.forEach { group ->
                val values = groupRowValues(group, i, singleRound)
                if (values == null) {
                    append("<td colspan=\"").append(group.headers.size).append("\">").append(if (i == 0) "Žádný výkon." else "").append("</td>")
                    return@forEach
                }
                values.forEachIndexed { column, value ->
                    append("<td>")
                    if (column == group.accentColumn) append("<strong>")
                    append(escapeHtml(value))
                    if (column == group.accentColumn) append("</strong>")
                    append("</td>")
                }
            }
            append("</tr>")
        }
        append("</table>")
    }
}

private data class GeneratedStats(val result: SeasonStatsResponseDto, val title: String, val subtitle: String, val singleRound: Boolean)

@Composable
fun SeasonStatsScreen(token: String?, role: String?, activeSeason: SeasonDto?, onOpenNews: (String) -> Unit) {
    var seasons by remember { mutableStateOf<List<SeasonDto>>(emptyList()) }
    var seasonId by remember { mutableStateOf(activeSeason?.id ?: "") }
    var leagues by remember { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var matches by remember { mutableStateOf<List<MatchWithLeagueDto>>(emptyList()) }
    var leagueId by remember { mutableStateOf(ALL_LEAGUES) }
    var periodValue by remember { mutableStateOf("season") }
    var topN by remember { mutableStateOf(3) }
    var sectionOptions by remember { mutableStateOf<Map<String, StatsOptions>>(emptyMap()) }
    var generated by remember { mutableStateOf<GeneratedStats?>(null) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var showUnplayed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val views = selectedViews(sectionOptions, topN)

    LaunchedEffect(Unit) {
        try {
            seasons = Api.fetchSeasons(token)
            if (seasonId.isBlank()) seasonId = seasons.firstOrNull()?.id ?: ""
        } catch (e: Throwable) {
            error = "Načtení sezón selhalo: ${e.apiMessage()}"
        }
    }

    LaunchedEffect(seasonId) {
        if (seasonId.isBlank()) return@LaunchedEffect
        leagueId = ALL_LEAGUES
        periodValue = "season"
        try {
            leagues = Api.fetchLeagues(token, seasonId)
            matches = Api.fetchMatchesForSeason(token, seasonId)
        } catch (e: Throwable) {
            error = "Načtení lig/zápasů selhalo: ${e.apiMessage()}"
        }
    }

    val scopedMatches = matches.filter { leagueId == ALL_LEAGUES || it.seasonLeagueId == leagueId }
    val parts = PARTS.filter { (part, _) -> scopedMatches.any { statsPart(it.phase) == part } }
    val rounds = scopedMatches
        .map { statsPart(it.phase) to it.roundNumber }
        .distinct()
        .sortedWith(compareBy({ phaseOrder(if (it.first == "playoff") "playoff_title" else it.first) }, { it.second }))

    Div(attrs = { classes("card") }) {
        H2 { Text("Statistiky") }
        if (!role.canManage()) {
            Text("Statistiky jsou dostupné jen adminovi/superadminovi.")
            return@Div
        }

        Div(attrs = { classes("actions") }) {
            Label { Text("Sezóna: ") }
            Select(attrs = { onChange { seasonId = it.value ?: "" } }) {
                seasons.forEach { s -> Option(s.id, attrs = { if (s.id == seasonId) selected() }) { Text(s.name) } }
            }
            Label { Text("Liga: ") }
            Select(attrs = {
                onChange {
                    leagueId = it.value ?: ALL_LEAGUES
                    // The chosen part/round may not exist in the new league.
                    periodValue = "season"
                }
            }) {
                Option(ALL_LEAGUES, attrs = { if (leagueId == ALL_LEAGUES) selected() }) { Text("Všechny ligy") }
                leagues.forEach { l -> Option(l.id, attrs = { if (l.id == leagueId) selected() }) { Text(l.name) } }
            }
        }
        Div(attrs = { classes("actions") }) {
            Label { Text("Období: ") }
            Select(attrs = { onChange { periodValue = it.value ?: "season" } }) {
                Option("season", attrs = { if (periodValue == "season") selected() }) { Text("Celá sezóna") }
                OptGroup("Část soutěže") {
                    parts.forEach { (part, label) ->
                        val v = "part:$part"
                        Option(v, attrs = { if (periodValue == v) selected() }) { Text(label) }
                    }
                }
                OptGroup("Kolo") {
                    rounds.forEach { (part, round) ->
                        val v = "round:$part:$round"
                        Option(v, attrs = { if (periodValue == v) selected() }) { Text("${partLabel(part)} — kolo $round") }
                    }
                }
            }
            Label { Text("Žebříček: ") }
            Select(attrs = { onChange { topN = it.value?.toIntOrNull() ?: 3 } }) {
                STATS_LIMITS.forEach { n ->
                    Option(n.toString(), attrs = { if (n == topN) selected() }) { Text(if (n == 1) "jen vítěz" else "TOP $n") }
                }
            }
            Button(attrs = {
                if (busy || seasonId.isBlank()) disabled()
                onClick {
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            val period = parsePeriod(periodValue)
                            val result = Api.fetchSeasonStats(
                                token,
                                // Load the largest selectable rank once; display limits can then change immediately.
                                SeasonStatsRequest(seasonId, leagueId.ifBlank { null }, period.phase, period.roundNumber, STATS_LIMITS.last()),
                            )
                            val seasonName = seasons.firstOrNull { it.id == seasonId }?.name ?: ""
                            val leagueText = leagues.firstOrNull { it.id == leagueId }?.name ?: "všechny ligy"
                            val label = periodLabel(period, seasonName)
                            generated = GeneratedStats(
                                result = result,
                                title = "Statistiky: $label${if (leagueId == ALL_LEAGUES) "" else " ($leagueText)"}",
                                subtitle = "Období: $label · ${leagueText.replaceFirstChar { it.uppercase() }}",
                                singleRound = period.roundNumber != null,
                            )
                            showUnplayed = false
                        } catch (e: Throwable) {
                            error = "Výpočet statistik selhal: ${e.apiMessage()}"
                        }
                        busy = false
                    }
                }
            }) { Text(if (busy) "Počítám…" else "Vygenerovat") }
        }
        H3 { Text("Sekce výstupu") }
        Table(attrs = { classes("public-table") }) {
            Tr {
                Th { Text("Statistika") }
                Th { Text("Zahrnout") }
                Th { Text("Členění") }
                Th { Text("Detaily") }
                Th { Text("Počet záznamů") }
            }
            CATEGORIES.forEach { cat ->
                val key = cat.sections.first().key
                val option = sectionOptions[key] ?: StatsOptions()
                Tr {
                    Td { Text(cat.title) }
                    Td {
                        CheckboxInput(checked = option.enabled) {
                            attr("aria-label", "Zahrnout: ${cat.title}")
                            onInput { sectionOptions = sectionOptions + (key to option.copy(enabled = it.value)) }
                        }
                    }
                    Td {
                        if (cat.mergedKey != null) Select(attrs = {
                            attr("aria-label", "Pohlaví: ${cat.title}")
                            if (!option.enabled) disabled()
                            onChange { sectionOptions = sectionOptions + (key to option.copy(genderMode = it.value ?: "split")) }
                        }) {
                            listOf("split" to "Muži / ženy zvlášť", "all" to "Společně bez ohledu na pohlaví", "male" to "Jen muži", "female" to "Jen ženy").forEach { (value, label) ->
                                Option(value, attrs = { if (option.genderMode == value) selected() }) { Text(label) }
                            }
                        } else if (cat.byLeagueKey != null) Select(attrs = {
                            attr("aria-label", "Členění: ${cat.title}")
                            if (!option.enabled) disabled()
                            onChange { sectionOptions = sectionOptions + (key to option.copy(byLeague = it.value == "league")) }
                        }) {
                            Option("overall", attrs = { if (!option.byLeague) selected() }) { Text("Celkem") }
                            Option("league", attrs = { if (option.byLeague) selected() }) { Text("Podle ligy") }
                        } else Text("Společně")
                    }
                    Td {
                        if (cat.kind == "distribution" && option.byLeague) Text("Bez detailů (matice)")
                        else CheckboxInput(checked = option.details) {
                            attr("aria-label", "Detaily: ${cat.title}")
                            if (!option.enabled) disabled()
                            onInput { sectionOptions = sectionOptions + (key to option.copy(details = it.value)) }
                        }
                    }
                    Td {
                        if (cat.isAggregate()) Text("Všechny")
                        else Select(attrs = {
                            attr("aria-label", "Počet záznamů: ${cat.title}")
                            if (!option.enabled) disabled()
                            onChange { sectionOptions = sectionOptions + (key to option.copy(limit = it.value?.toIntOrNull())) }
                        }) {
                            Option("", attrs = { if (option.limit == null) selected() }) { Text("Podle hlavního ($topN)") }
                            STATS_LIMITS.forEach { n ->
                                Option(n.toString(), attrs = { if (option.limit == n) selected() }) { Text(n.toString()) }
                            }
                        }
                    }
                }
            }
        }
        Div(attrs = { classes("muted") }) {
            Text("Při shodě na posledním místě žebříčku se zobrazí všechny stejné výkony. Kontumované zápasy se nepočítají.")
        }
        Div(attrs = { classes("muted") }) {
            Text("Nastavení sekcí platí pro náhled i aktualitu a mění se bez nového výpočtu. Společné žebříčky zahrnují i hráče bez vyplněného pohlaví. ")
            Text("Těsný duel má rozdíl nejvýše 10 kuželek, oba hráči musí nastoupit. Úspěšnost = (výhry + remízy) / počet těsných duelů. Minimum je 30 % nejvyššího počtu těsných duelů hráče ve výběru, zaokrouhleno nahoru. ")
            Text("Nejlepší bodovač = (výhry + remízy) / odehrané duely; remíza přináší bod a počítá se jako výhra. Minimum je 30 % možných her (3 hry za každý odehraný nekontumovaný zápas družstva ve výběru), zaokrouhleno nahoru. Počty a minima jsou v detailech.")
        }
        Div(attrs = { classes("muted") }) {
            Text("Vlastní počet záznamů přebíjí hlavní nastavení. U žebříčků zůstávají všechny shody na posledním místě. Úzké tabulky se automaticky spojují s následující stejného typu (jednotlivci, družstva, zápasy nebo souhrny), pokud mají dohromady nejvýše 10 sloupců. ")
            Text("Agregace vždy zobrazují všechny řádky. Průměrný nához = součet kuželek / počet skutečně odehraných her. Skóre zápasů je uvedeno jako vyšší:nižší (5:4 zahrnuje i 4:5). Rozdělení podle ligy má podobu matice skóre × liga s počty zápasů, bez detailů.")
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    }

    val g = generated ?: return
    val r = g.result
    val singleRound = g.singleRound

    Div(attrs = { classes("card") }) {
        H3 { Text(g.title) }
        if (r.unplayed.isNotEmpty()) {
            Div(attrs = { classes("editor-error") }) {
                Text("Pozor: ${r.unplayed.size} z ${r.totalMatches} zápasů v tomto výběru ještě není odehráno — statistiky jsou jen z odehraných. ")
                Span(attrs = {
                    classes("match-modal-photo-link")
                    onClick { showUnplayed = !showUnplayed }
                }) { Text(if (showUnplayed) "Skrýt seznam" else "Zobrazit seznam") }
            }
            if (showUnplayed) {
                Ul {
                    r.unplayed.forEach { m ->
                        Li { Text("${m.leagueName} · ${roundSlotLabel(m.phase, m.roundNumber)}: ${m.homeTeamName} – ${m.awayTeamName}") }
                    }
                }
            }
        } else if (r.totalMatches == 0) {
            Div { Text("V tomto výběru nejsou žádné zápasy.") }
        }
        if (r.playersWithoutGender.isNotEmpty()) {
            Div(attrs = { classes("editor-error") }) {
                Text(
                    "Tito hráči nemají vyplněné pohlaví, a proto nejsou ve statistikách muži/ženy (doplň v Hráči): " +
                        r.playersWithoutGender.joinToString(", ")
                )
            }
        }

        if (views.isEmpty()) Div(attrs = { classes("muted") }) { Text("Vyberte alespoň jednu sekci výstupu.") }
        statsTables(r, views, singleRound).forEach { table ->
            val groups = table.groups
            if (table.views.size == 1) H3 { Text(table.views.first().category.title) }
            // Every group after the first starts with a vertical divider (.stats-split).
            val split: AttrsScope<*>.(Int) -> Unit = { g -> if (g > 0) classes("stats-split") }
            if (groups.all { it.rowCount == 0 }) {
                Div(attrs = { classes("muted") }) { Text("Žádný výkon.") }
            } else Table(attrs = { classes("public-table", "stats-table") }) {
                if (groups.size > 1) {
                    Tr {
                        groups.forEachIndexed { g, group ->
                            Th(attrs = { split(g); attr("colspan", group.headers.size.toString()) }) {
                                Text(if (table.views.size > 1) group.label else group.section.label ?: "")
                            }
                        }
                    }
                }
                Tr {
                    groups.forEachIndexed { g, group ->
                        group.headers.forEachIndexed { column, label ->
                            Th(attrs = {
                                if (column == 0) split(g)
                                if (column == group.accentColumn) classes("col-accent")
                            }) { Text(label) }
                        }
                    }
                }
                repeat(groups.maxOf { it.rowCount }) { i ->
                    Tr {
                        groups.forEachIndexed { g, group ->
                            val values = groupRowValues(group, i, singleRound)
                            if (values == null) {
                                Td(attrs = { split(g); classes("muted"); attr("colspan", group.headers.size.toString()) }) {
                                    if (i == 0) Text("Žádný výkon.")
                                }
                            } else {
                                values.forEachIndexed { column, value ->
                                    Td(attrs = { if (column == 0) split(g) }) {
                                        if (column == group.accentColumn) Span(attrs = { classes("pill") }) { Text(value) }
                                        else Text(value)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                if (busy || views.isEmpty()) disabled()
                onClick {
                    busy = true
                    error = null
                    scope.launch {
                        try {
                            val created = Api.createNews(
                                token,
                                NewsItemInput(g.title, buildNewsHtml(r, g.subtitle, views, g.singleRound), isPinned = false, publishedAt = null),
                                source = "stats",
                            )
                            onOpenNews(created.id)
                        } catch (e: Throwable) {
                            error = "Vytvoření aktuality selhalo: ${e.apiMessage()}"
                            busy = false
                        }
                    }
                }
            }) { Text("Vytvořit aktualitu (koncept)") }
        }
        error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
    }
}
